import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import type { ChatDocGroupRow } from '../../types';

// ── Admin → Chat: which group is the Active Jobs group (report 8e70cd7c) ─────────────────────────────────────────
// The reporter asked for comments made in Active Jobs to be brought into "the active jobs group", asking the job's
// resource to answer there; asked which group that is, he answered "the admin settings so I can select the group for
// you and yes, the group already exists" — so this is where it is selected.
// The document upload group's card (ChatDocGroup) over again: one radio down the list of the chat's groups, plus None,
// which is the state it starts in and means nothing goes from Active Jobs into Chat. Nothing about a group is written
// into the code: the list is the groups themselves, so the next firm marks whichever of their own groups they like.
// The server keeps the choice as one app_settings value (routes/chat.ts 'chat_jobs_group') — no table, no column.
// Saved on the press, as that card is. The group rows read the same lines as the two cards above it (cdg_unnamed /
// cdg_members / cdg_made_by): one group is one group, however it is being chosen.

export default function ChatJobsGroup() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<ChatDocGroupRow[] | null>(null);
  const [groupId, setGroupId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.chatJobsGroup()
      .then(d => { setGroups(d.groups || []); setGroupId(d.groupId || ''); })
      .catch((e: any) => { setGroups([]); setErr(e.message); });
  }, []);

  const pick = async (id: string) => {
    if (busy || id === groupId) return;
    const was = groupId;
    setGroupId(id); setErr(''); setDone(false); setBusy(true);
    try { const r = await api.chatSetJobsGroup(id); setGroupId(r.groupId || ''); setDone(true); }
    catch (e: any) { setGroupId(was); setErr(e.message); }
    finally { setBusy(false); }
  };

  const line: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '9px 2px',
    borderTop: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text-primary)', cursor: busy ? 'wait' : 'pointer',
  };

  return (
    <div style={{ maxWidth: 820, margin: '16px auto 0' }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--bg-surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <i className="ti ti-briefcase" style={{ fontSize: 20, color: 'var(--text-secondary)' }} aria-hidden />
          <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t('cjg_title')}</div>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>{t('cjg_desc')}</div>

        {groups === null ? (
          !err && <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" aria-hidden /></div>
        ) : (
          <>
            <label style={line}>
              <input type="radio" name="cjg" checked={!groupId} onChange={() => pick('')} disabled={busy} />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 700 }}>{t('cdg_none')}</span>
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('cjg_none_hint')}</span>
              </span>
            </label>
            {groups.map(g => (
              <label key={g.id} style={line}>
                <input type="radio" name="cjg" checked={groupId === g.id} onChange={() => pick(g.id)} disabled={busy} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 700 }}>{g.name || t('cdg_unnamed')}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-tertiary)' }}>
                    {t('cdg_members', { n: g.members })}{g.createdByName ? ` · ${t('cdg_made_by', { name: g.createdByName })}` : ''}
                  </span>
                </span>
              </label>
            ))}
            {groups.length === 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '10px 0', borderTop: '1px solid var(--border)' }}>{t('cdg_no_groups')}</div>
            )}
          </>
        )}

        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginTop: 10 }}>{err}</div>}
        {done && !err && <div style={{ fontSize: 12, color: '#15803D', marginTop: 10 }}>{t('cdg_saved')}</div>}
      </div>
    </div>
  );
}
